import {useCallback, useEffect, useRef, useState} from 'react';
import {getWindow} from '../lib/utils';
import {useTimeoutRef} from './use-timeout-ref';

function useIsFirstRender(): boolean {
  const isFirst = useRef(true);

  if (isFirst.current) {
    isFirst.current = false;

    return true;
  }

  return isFirst.current;
}

type UseModalControlsOptions = {
  key: string;
  enabled?: boolean;
  initState?: boolean;
};

export function useModalWithHistoryControls({
  key,
  initState = false,
  enabled = true,
}: UseModalControlsOptions) {
  const isFirstRender = useIsFirstRender();
  const [isOpen, setIsOpen] = useState(initState);
  const scrollRef = useRef(0);
  const resetDelayRef = useRef(false);
  const resetDelayTimeoutRef = useTimeoutRef();

  const addHash = useCallback(() => {
    const targetWindow = getWindow();
    const historyState = targetWindow.history.state;

    if (enabled && historyState?.popupKey !== key) {
      scrollRef.current = targetWindow.scrollY;
      targetWindow.history.pushState(
        {
          ...historyState,
          popupKey: key,
          isPopupOpen: true,
        },
        '',
        targetWindow.location.pathname,
      );
      targetWindow.scrollTo(0, scrollRef.current);
    }
  }, [enabled, key]);

  const openModal = useCallback(() => {
    setIsOpen(true);
    addHash();
  }, [addHash]);

  const resetHash = useCallback(() => {
    const targetWindow = getWindow();
    const historyState = targetWindow.history.state;

    if (enabled && historyState?.popupKey === key && !resetDelayRef.current) {
      targetWindow.history.back();

      resetDelayRef.current = true;

      resetDelayTimeoutRef.current = setTimeout(() => {
        resetDelayRef.current = false;
      }, 300);
    }
  }, [enabled, key, resetDelayTimeoutRef]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    resetHash();
  }, [resetHash]);

  const toggleModal = useCallback(() => {
    setIsOpen(!isOpen);
    if (isOpen) {
      resetHash();
    } else {
      addHash();
    }
  }, [addHash, isOpen, resetHash]);

  useEffect(() => {
    const targetWindow = getWindow();

    const handler = () => {
      if (enabled && isOpen) {
        setIsOpen(false);
      }
    };

    targetWindow.addEventListener('popstate', handler, false);
    return () => {
      targetWindow.removeEventListener('popstate', handler, false);
    };
  }, [enabled, isOpen]);

  useEffect(
    function resetDeprecatedState() {
      const targetWindow = getWindow();
      const historyState = targetWindow.history.state;

      if (isFirstRender && !isOpen && historyState?.popupKey === key) {
        targetWindow.history.back();
      }
    },
    [isFirstRender, isOpen, key],
  );

  return {
    isOpen,
    openModal,
    closeModal,
    toggleModal,
    setIsOpen,
  };
}
